import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  useNotificationSettingsQuery,
  useConnectTelegramMutation,
  useDisconnectTelegramMutation,
} from '../../../hooks/useNotificationSettings';
import { openLinkInNewTab } from '../../../common/utils/openLinkInNewTab';

interface Props {
  /** Color scheme for the host surface. 'dark' = cabinet, 'light' = dashboard. */
  variant?: 'light' | 'dark';
}

export const TelegramLoginCodesSection = ({ variant = 'dark' }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useNotificationSettingsQuery();
  const connect = useConnectTelegramMutation();
  const disconnect = useDisconnectTelegramMutation();

  const textColor = variant === 'dark' ? '#fff' : '#0f172a';
  const subColor = variant === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b';

  // Open the tab synchronously inside the click gesture (via openLinkInNewTab)
  // and redirect it once the deep link resolves, so the popup isn't blocked.
  const handleConnect = () =>
    openLinkInNewTab(async () => (await connect.mutateAsync()).deepLink);

  const busy = connect.isPending || disconnect.isPending;

  return (
    <Stack
      gap={1.5}
      sx={{
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: variant === 'dark' ? 'rgba(255,255,255,0.15)' : '#e2e8f0',
        background: variant === 'dark' ? 'transparent' : '#fff',
      }}
    >
      <Stack gap={0.5}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: textColor }}>
          {t('telegramSettings.title')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: subColor, lineHeight: 1.45 }}>
          {t('telegramSettings.description')}
        </Typography>
      </Stack>

      {isLoading || !data ? (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={22} sx={{ color: '#22c55e' }} />
        </Stack>
      ) : data.telegramLinked ? (
        <Stack gap={1.5}>
          <Typography sx={{ fontSize: 14, color: textColor }}>
            {t('telegramSettings.linkedLabel')}
          </Typography>
          <Button
            variant="text"
            color="error"
            disabled={busy}
            onClick={() => disconnect.mutate()}
            sx={{ alignSelf: 'flex-start', paddingX: 0 }}
          >
            {t('telegramSettings.disconnectBtn')}
          </Button>
          {disconnect.isError && (
            <Typography sx={{ fontSize: 12, color: '#dc2626' }}>
              {t('telegramSettings.updateError')}
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack gap={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }} sx={{ width: '100%' }}>
          <Button
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={handleConnect}
            startIcon={
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                  flexShrink: 0,
                }}
              >
                <img
                  src="/telegram-logo.png"
                  alt=""
                  width={22}
                  height={22}
                  style={{ display: 'block' }}
                />
              </Box>
            }
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minHeight: 52,
              paddingY: '11px',
              paddingX: '22px',
              gap: '6px',
              fontWeight: 600,
              fontSize: { xs: 16, sm: 15 },
              letterSpacing: '0.01em',
              borderRadius: '14px',
              color: '#000',
              boxShadow: 'none',
              transition: 'transform 0.18s ease, background-color 0.4s ease',
              '@media (hover: hover)': {
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 'none',
                },
              },
              '&:active': { transform: 'scale(0.99)' },
              '&.Mui-disabled': { boxShadow: 'none' },
            }}
          >
            {connect.isPending
              ? t('telegramSettings.connecting')
              : t('telegramSettings.connectBtn')}
          </Button>
          {connect.isSuccess && (
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography sx={{ fontSize: 12, color: subColor }}>
                {t('telegramSettings.refreshHint')}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => refetch()}
                sx={{ color: '#22c55e', paddingX: 0, minWidth: 'auto' }}
              >
                {t('telegramSettings.refreshBtn')}
              </Button>
            </Stack>
          )}
          {connect.isError && (
            <Typography sx={{ fontSize: 12, color: '#dc2626' }}>
              {t('telegramSettings.connectError')}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};
